import React, { useState } from 'react'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useIsMobile } from '../../../../../hooks/useIsMobile'
import { cn } from '../../../../../helpers/utils'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import { ITicket } from '../../../../../interfaces/models/ticket.interfaces'
import { Icon, IconChoices } from '../../../../../components/common/Icon/Icon'
import { TicketInformation } from './TicketInformation'
import {
  Button,
  ButtonVariant,
} from '../../../../../components/common/Button/Button'
import { IDocumentModel } from '../../../../../interfaces/models/documents.interfaces'
import { downloadDocumentFile } from '../../../../../api/documents'
import { theme } from '../../../../../theme/theme'
import { infiniteTicketQueries } from '../../../../../api/ticket'
import { InfiniteScroll } from '../../../../../components/common/InfiniteScroll'
import { ErrorView } from '../../../../error/ErrorView'
import { IllustrationChoices } from '../../../../../components/common/Illustration'
import { TicketImages } from '../TicketImages/TicketImages'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'

const Settings = ({
  ticket,
  openDocuments,
  openImages,
}: {
  ticket?: ITicket
  openImages?: () => void
  openDocuments?: () => void
}): React.ReactNode => {
  const { translate } = useAppTranslation()

  return (
    <>
      {ticket && <TicketInformation className="mb-4" ticket={ticket} />}
      <button
        onClick={openImages}
        className="flex m-0 gap-2 py-4 rounded border-0 hover:bg-gray-light"
      >
        <Icon icon={IconChoices.IMAGE} />
        {translate('ticketSidebar.images')} ({ticket?.totalImages || 0})
      </button>
      <button
        onClick={openDocuments}
        className="flex m-0 gap-2 py-4 rounded border-0 hover:bg-gray-light mb-6"
      >
        <Icon icon={IconChoices.DOCUMENT} />
        {translate('ticketSidebar.documents')} ({ticket?.totalDocuments || 0})
      </button>
    </>
  )
}

const Images = ({
  ticket,
  goBack,
}: {
  ticket?: ITicket
  goBack: () => void
}): React.ReactNode => {
  const { translate } = useAppTranslation()

  return (
    <div>
      <div className="flex w-full justify-start gap-2 items-center pl-6">
        <Button
          icon={IconChoices.ARROW_LEFT}
          variant={ButtonVariant.TEXT}
          onClick={goBack}
        />
        <h2 style={theme.textVariants.baseBold}>
          {translate('ticketSidebar.imagesTitle')}
        </h2>
      </div>
      <div className="px-6">
        <TicketImages ticket={ticket} />
      </div>
    </div>
  )
}

const DocumentItem = ({
  document,
}: {
  document: IDocumentModel
}): React.ReactNode => {
  const { mutate: downloadDocument, isPending } = useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      downloadDocumentFile(id, filename),
  })

  return (
    <button
      key={document._id}
      className={`flex min-w-0 flex-row items-center gap-3 whitespace-nowrap rounded
                    bg-neutral-96 py-4 px-5 ${
                      !isPending ? 'hover:bg-neutral-90' : ''
                    }`}
      onClick={() =>
        downloadDocument({ id: document._id, filename: document.filename })
      }
      disabled={isPending}
    >
      <Icon icon={IconChoices.DOCUMENT} size={24} />
      <span
        className="overflow-hidden text-ellipsis"
        style={theme.textVariants.baseBold}
      >
        {document.name}
      </span>
      <Icon icon={IconChoices.ARROW} size={24} highlighted />
    </button>
  )
}
const Documents = ({
  ticketId,
  societyId,
  goBack,
}: {
  ticketId?: string
  societyId?: string
  goBack: () => void
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const {
    data: query,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(
    infiniteTicketQueries.getTicketDocuments({
      ticketId,
      societyId,
    })
  )
  const documents = query?.pages.flatMap((page) => page.items) || []
  return (
    <div>
      <div className="flex gap-2 justify-start w-full items-center pl-6">
        <Button
          icon={IconChoices.ARROW_LEFT}
          variant={ButtonVariant.TEXT}
          onClick={goBack}
        />
        <h2 style={theme.textVariants.baseBold}>
          {translate('ticketSidebar.documentsTitle')}
        </h2>
      </div>
      <div className="px-6 overflow-auto">
        <InfiniteScroll
          key="infiniteDocumentScroll"
          onEndReached={() => fetchNextPage({ cancelRefetch: false })}
          isFetching={isFetching}
          className="flex flex-col gap-2"
        >
          {!documents.length && !isFetching && (
            <ErrorView
              title={translate('ticketSidebar.emptyDocumentsTitle')}
              illustration={IllustrationChoices.EMPTY}
            />
          )}
          {documents.map((document) => {
            return <DocumentItem key={document._id} document={document} />
          })}
        </InfiniteScroll>
      </div>
    </div>
  )
}

interface SideBarProps {
  className?: string
  society?: ISocietiesModel
  ticket?: ITicket
  isOverlay?: boolean
  isOpen?: boolean
  onClose?: () => void
}

export const TicketSideBar = ({
  className,
  society,
  ticket,
  isOverlay = false,
  isOpen = true,
  onClose,
}: SideBarProps): React.ReactNode => {
  const isMobile = useIsMobile()

  const [view, setView] = useState<'settings' | 'documents' | 'images'>(
    'settings'
  )
  const isVisible = isOverlay ? isOpen : true

  return (
    <>
      {isOverlay && isVisible && (
        <button
          type="button"
          className="fixed bottom-0 left-0 right-0 top-[52px] z-20 m-0 border-0 bg-black/40 p-0 md:top-[64px]"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          'w-sidebar min-w-sidebar flex-col border-l bg-white overflow-y-auto min-h-0',
          isOverlay &&
            'fixed right-0 z-30 transform shadow-xl transition-transform duration-300 ease-in-out',
          isOverlay && (isVisible ? 'translate-x-0' : 'translate-x-full'),
          isOverlay && !isVisible && 'pointer-events-none',
          className
        )}
        style={{
          height: `calc(100vh - ${isMobile ? 52 : 64}px)`,
          top: isOverlay ? `${isMobile ? 52 : 64}px` : undefined,
        }}
      >
        {isOverlay && (
          <div className="flex justify-end p-4 pb-0">
            <button
              type="button"
              onClick={onClose}
              className="m-0 rounded border-0 bg-transparent p-0"
            >
              <Icon icon={IconChoices.CROSS} />
            </button>
          </div>
        )}
        {view === 'settings' && (
          <div className={isOverlay ? 'px-6 pb-6 pt-4' : 'p-6'}>
            <Settings
              ticket={ticket}
              openDocuments={() => setView('documents')}
              openImages={() => setView('images')}
            />
          </div>
        )}

        {view === 'images' && (
          <Images ticket={ticket} goBack={() => setView('settings')} />
        )}
        {view === 'documents' && (
          <Documents
            ticketId={ticket?.id}
            societyId={society?._id}
            goBack={() => setView('settings')}
          />
        )}
      </div>
    </>
  )
}
